/**
 * PendingInvoiceTable — S4 / FR-4 (proposal §3.10). Exactly five columns:
 *
 *   INVOICE · CUSTOMER · TPIN/BRANCH · SUBMITTED · STATUS
 *
 * No error-code / error-message columns — the source holds none for
 * pending invoices (design README S4 rule; principle 4: no additional
 * payload columns).
 *
 * STATUS carries a "Pending" badge in `--pending` / `--pending-tint`
 * (tokenised per proposal §3.3 unmapped-marker precedent). The aging
 * signal lives in the SUBMITTED cell — formatTimestamp primary +
 * muted relative age below.
 */

import type { InvoiceRow } from '@domain/types'
import { formatTimestamp } from '@domain/dates'
import { NOT_RECORDED_TEXT, heldValue } from './absentValue'
import { InvoiceIdLink } from './InvoiceIdLink'
import { PendingStatus } from './PendingStatus'

export function PendingInvoiceTable({
  rows,
  dbName,
  now,
}: {
  rows: readonly InvoiceRow[]
  dbName: string
  /** Reference clock for the relative-age computation; injected so the
   *  formatter stays pure and the table trivially testable. */
  now: Date
}) {
  return (
    <table className="invoice-table pending-invoice-table" data-testid="pending-invoice-table">
      <thead>
        <tr>
          <th scope="col">Invoice</th>
          <th scope="col">Customer</th>
          <th scope="col">TPIN / branch</th>
          <th scope="col">Submitted</th>
          <th scope="col">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          // Null and the empty string both mean the source holds none (the shared absent-value rule).
          const ts = formatTimestamp(heldValue(row.submissionTimestamp), now)
          return (
            <tr key={row.rectNo} data-testid={`pending-row-${row.rectNo}`}>
              <td>
                <InvoiceIdLink dbName={dbName} rectNo={row.rectNo} testid={`pending-id-${row.rectNo}`}>
                  {row.rectNo}
                </InvoiceIdLink>
              </td>
              <td className="customer-cell">{dbName}</td>
              <td className="nowrap">
                {row.tin} / {row.bhfId}
              </td>
              <td>
                {ts.absolute === null ? (
                  <span className="when">
                    <span>{NOT_RECORDED_TEXT}</span>
                  </span>
                ) : (
                  <span className="when">
                    <span>{ts.absolute}</span>
                    {ts.relative !== null && <span className="ago">{ts.relative}</span>}
                  </span>
                )}
              </td>
              <td>
                <PendingStatus rectNo={row.rectNo} resultCd={row.resultCd} />
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
